import { Component, HostBinding, Input, OnInit } from '@angular/core'
import { trigger, state, style, transition, animate } from '@angular/animations'
import { Issue } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { getResourceNameFromArn, getResourceTypeDisplayString } from 'src/app/shared/utils'
import { environment } from 'src/environments/environment'
import {
  AcmCertificateNotFoundIssue,
  AmiStorageNotAccessibleIssue,
  ClientErrorIssue,
  KeyPairNotFoundIssue,
  MarketplaceAmiCantBeCopiedIssue,
  MultipleMatchingImagesIssue,
  QuotaIssue,
  RefreshClientErrorIssue,
} from 'src/app/account/services/issue'
import { SyncPair } from 'src/app/account/services/sync-pair.service'
import { ViewableIssue } from 'src/app/account/components/issues-view/issues-view.component'
import { Application } from '../../services/application.service'
import { consoleUrl } from '../../../../utils/aws-utils'
import { CopyFeedbackService } from 'src/app/shared/copy-feedback.service'

// tslint:disable:component-selector

const accordionSlide = trigger('accordionSlide', [
  state('collapsed', style({ height: '0px', paddingTop: '0', paddingBottom: '0' })),
  state('expanded', style({ height: '*', paddingTop: '*', paddingBottom: '*' })),
  transition('collapsed <=> expanded', animate('200ms ease-in-out')),
])

@Component({
  selector: 'issue',
  templateUrl: './issue-view.component.html',
  styleUrls: ['./issue-view.component.scss'],
  animations: [accordionSlide],
  standalone: false,
  providers: [CopyFeedbackService],
})
export class IssueViewComponent implements OnInit {
  @Input() issue: ViewableIssue = null
  // tslint:disable-next-line:no-input-rename
  @Input('sync-pair') syncPair: SyncPair = null
  // tslint:disable-next-line:no-input-rename
  @Input('application') application: Application = null
  @Input() grouped = false
  // tslint:disable-next-line:variable-name
  // Used by scrollToIssue() in ApplicationComponent
  @HostBinding('attr.id') get issueElementId(): string {
    return this.issue?.issueId ? `issue-${this.issue.issueId}` : null
  }
  @HostBinding('class.expanded') _panelDisplayed = false
  @HostBinding('class.grouped') get isGrouped() {
    return this.grouped
  }

  constructor(public copyFeedback: CopyFeedbackService) {}

  ngOnInit() {}

  togglePanelView() {
    this._panelDisplayed = !this._panelDisplayed
  }

  expand() {
    this._panelDisplayed = true
  }

  collapse() {
    this._panelDisplayed = false
  }

  get panelDisplayed() {
    return this._panelDisplayed
  }

  get resourceDisplayDetail() {
    let displayDetail = ''
    if (!!this.issue.resourceType) {
      displayDetail = `${getResourceTypeDisplayString(this.issue.resourceType, this.issue.resourceVariant)}: ${this.issue.resourceName}`
    }
    return displayDetail
  }

  get resourceTypeString() {
    let resourceTypeString = ''
    if (!!this.issue.resourceType) {
      resourceTypeString = getResourceTypeDisplayString(this.issue.resourceType, this.issue.resourceVariant)
    }
    return resourceTypeString
  }

  get resourceNameString() {
    let resourceNameStr
    if (!this.issue.resourceName && this.issue.arn) {
      resourceNameStr = getResourceNameFromArn(this.issue.arn)
    } else resourceNameStr = this.issue.resourceName
    return resourceNameStr
  }

  get consoleUrl() {
    return consoleUrl(this.issue.arn)
  }

  onResourceNameCopied() {
    this.copyFeedback.onCopied('resource')
  }
}

@Component({
  selector: 'certificate-not-found-issue',
  template:
    `A certificate for <code class="code">{{ issue.domain }}</code> could not be found in the recovery environment.` +
    ` Certificates need to be manually provisioned in the recovery environment` +
    ` for ${environment.productName} to use.`,
  standalone: false,
})
export class CertificateNotFoundIssueComponent implements OnInit {
  @Input() issue: AcmCertificateNotFoundIssue = null

  constructor() {}

  ngOnInit() {}
}

@Component({
  selector: 'client-error-issue',
  template: `<span class="strong-text">Client Error Issue:</span> An error occurred during a call to
    {{ issue.operation }} <br /><span class="strong-text">Detailed message:</span>
    <code class="error-code-block">{{ issue.message }}</code>`,
  standalone: false,
})
export class ClientErrorIssueComponent implements OnInit {
  @Input() issue: ClientErrorIssue = null

  constructor() {}

  ngOnInit() {}
}

@Component({
  selector: 'refresh-client-error-issue',
  template: `<span class="strong-text">Refresh Client Error:</span> An error occurred during a call to
    {{ issue.operation }} on resource {{ issue.resourceType }} <br /><span class="strong-text">Detailed message:</span>
    <code class="error-code-block">{{ issue.message }}</code>`,
  standalone: false,
})
export class RefreshClientErrorIssueComponent implements OnInit {
  @Input() issue: RefreshClientErrorIssue = null

  constructor() {}

  ngOnInit() {}
}

@Component({
  selector: 'quota-issue',
  template:
    `A resource limit was encountered during the {{ issue.operation }} operation. ` +
    `Reduce your policy size or <a target="_blank" ` +
    `href="https://console.aws.amazon.com/support/cases#/create?issueType=service-limit-increase">` +
    `request a limit increase</a> from Amazon (if applicable).` +
    `<code class="error-code-block">{{ issue.errorMessage }}</code>`,
  standalone: false,
})
export class QuotaIssueComponent implements OnInit {
  @Input() issue: QuotaIssue = null

  constructor() {}

  ngOnInit() {}
}

@Component({
  selector: 'marketplace-ami-issue',
  template:
    `This AMI was acquired from the AWS Marketplace.  ` +
    `AWS Marketplace AMIs cannot be copied between regions.  To work past this, use the ` +
    `AWS Marketplace to acquire a matching AMI in your recovery environment and retry the backup.  ` +
    `When a match is found, the backup can complete and ${environment.productName} will use the ` +
    `matching AMI during recovery.  For a recovery AMI to match, it must have the same owner-id, ` +
    `name, architecture, platform, and image-type as this one.` +
    `<ul>@for (criterion of issue.matchCriteria | keyvalue ; track criterion.key) {
    <li><b style="font-weight: bold;">{{ criterion.key }}</b>: {{ criterion.value.join(', ') }}</li>
    }</ul>`,
  standalone: false,
})
export class MarketplaceAmiCantBeCopiedIssueComponent implements OnInit {
  @Input() issue: MarketplaceAmiCantBeCopiedIssue = null

  constructor() {}

  ngOnInit() {}
}

@Component({
  selector: 'multiple-matching-images-issue',
  template:
    `{{ issue.message }} ` +
    `<ul>@for (criterion of issue.matchCriteria | keyvalue ; track criterion.key) {
    <li><b style="font-weight: bold;">{{ criterion.key }}</b>: {{ criterion.value.join(', ') }}</li>
    }</ul>`,
  standalone: false,
})
export class MultipleMatchingImagesIssueComponent implements OnInit {
  @Input() issue: MultipleMatchingImagesIssue = null

  constructor() {}

  ngOnInit() {}
}

// MarketplaceInstanceCantBeCopiedIssueComponent is nearly deprecated; newer template
// versions allow us to handle this with snapshot (not AMI) copies, but it's here
// for customers with ancient templates.
@Component({
  selector: 'marketplace-instance-issue',
  template:
    `This instance was launched from an AMI that was acquired from ` +
    `the AWS Marketplace.  AWS Marketplace instances cannot ` +
    `be copied between regions.`,
  standalone: false,
})
export class MarketplaceInstanceCantBeCopiedIssueComponent implements OnInit {
  @Input() issue: Issue = null

  constructor() {}

  ngOnInit() {}
}

@Component({
  selector: 'ami-storage-not-accessible-issue',
  template:
    `You do not have access to the underlying storage (i.e. EBS snapshots) ` +
    `of this AMI, and therefore it cannot be copied to the recovery environment.  To work past this, ` +
    `you can either:` +
    `<ol>` +
    `<li>Ask the owner to grant you snapshot access, or</li>` +
    `<li>Ask the owner to share a compatible AMI with the same owner-id, name, architecture, ` +
    `platform, and image-type (details below) to your recovery account, or` +
    `<ul>@for (criterion of issue.matchCriteria | keyvalue ; track criterion.key) {
    <li><b style="font-weight: bold;">{{ criterion.key }}</b>: {{ criterion.value.join(', ') }}</li>
    }</ul>` +
    `</li>` +
    `<li>Launch an instance from this AMI and create your own AMI from that instance. Then, update ` +
    `your launch configs, launch templates, etc. to reference your own AMI rather than this one.</li>` +
    `</ol>`,
  standalone: false,
})
export class AmiStorageNotAccessibleIssueComponent implements OnInit {
  @Input() issue: Issue = null

  constructor() {}

  ngOnInit() {}
}

@Component({
  selector: 'key-pair-not-found-issue',
  template:
    `A key pair named <span class="strong-text">{{ issue.keyPairName }}</span> could not be found in the recovery ` +
    `environment. Key pairs and other "secrets" cannot be read from the production environment ` +
    `so you must provision them manually. You can either <a [href]="uiService.generateKeyPairInst` +
    `ructionsUrl" target="_blank">create a new key pair</a> or <a [href]="uiService.importKeyPairInstructionsUrl"` +
    ` target="_blank">use the private key to import the existing key pair to the recovery environment</a>`,
  standalone: false,
})
export class KeyPairNotFoundIssueComponent implements OnInit {
  @Input() issue: KeyPairNotFoundIssue = null

  constructor(public uiService: UiService) {}

  ngOnInit() {}
}

@Component({
  selector: 'default-issue',
  template: `<code class="error-code-block">{{ issue.message }}</code>`,
  standalone: false,
})
export class DefaultIssueComponent implements OnInit {
  @Input() issue: Issue = null

  constructor() {}

  ngOnInit() {}
}
